'use client'

import * as Accordion from '@radix-ui/react-accordion'
import React, { useContext } from 'react'

import { LinkContext } from '../../contexts'
import { THEME_COLORS_ENUM } from '../../types/colors'
import { cn } from '../../utils/cn'

import { ProgramCardContent, ProgramCardFooter, ProgramCardHeader } from './_components'

import type { IMProgramCardProps } from './_types/types'

/**
 * Splits program name into two lines if it contains "medante" and has at least 3 words
 * @param name - The program name to split
 * @returns Object with firstLine and secondLine (secondLine is null if no split needed)
 */
function splitProgramName(name: string = ''): { firstLine: string; secondLine: string | null } {
	const nameLower = name.toLowerCase()
	const words = name.trim().split(/\s+/)

	// Check if name contains "medante" and has at least 3 words
	if (nameLower.includes('medante') && words.length >= 3) {
		// Find the index of the word containing "medante"
		const medanteIndex = words.findIndex(word => word.toLowerCase().includes('medante'))

		if (medanteIndex !== -1) {
			// First line: up to and including the word with "medante"
			const firstLine = words.slice(0, medanteIndex + 1).join(' ')
			// Second line: everything after
			const secondLine = words.slice(medanteIndex + 1).join(' ')

			return { firstLine, secondLine: secondLine || null }
		}
	}

	// No split needed
	return { firstLine: name, secondLine: null }
}

/**
 * MProgramCard Component
 *
 * A comprehensive program card component with accordion functionality,
 * color theming, and rich content support.
 *
 * Features:
 * - 6 color theme variants (cyclamen, blue, tirquoise, fuchsia, sand, purple)
 * - Expandable/collapsible content with Radix UI Accordion
 * - Featured badge with rotating animation
 * - Price display with optional old price strikethrough
 * - Rich HTML content support with DOMPurify sanitization
 * - Lucide icons for program items
 * - Responsive design
 *
 * @example
 * ```tsx
 * <MProgramCard
 *   program={{
 *     id: 1,
 *     title: "Medante Vital",
 *     subtitle: "Ročný program",
 *     price: 299,
 *     oldPrice: 399,
 *     color: "cyclamen",
 *     featured: true,
 *     items: [
 *       {
 *         icon: "Heart",
 *         title: "Preventívne vyšetrenia",
 *         content: "<p>Kompletné preventívne vyšetrenia</p>",
 *         highlighted: true
 *       }
 *     ],
 *     orderUrl: "/volne-terminy?type=annual-program&id=1",
 *     expanded: false,
 *     url: "/programy/medante-vital"
 *   }}
 * />
 * ```
 */
const DEFAULT_EXPAND_CONTENT_LABEL = 'Viac informácií o balíku'
const DEFAULT_COLLAPSE_CONTENT_LABEL = 'Menej informácií o balíku'

const DefaultLink = 'a'

export const MProgramCard: React.FC<IMProgramCardProps> = ({
	program,
	className,
	style,
	iconsSpriteUrl,
	splitTitle = true,
	eyebrow,
	expandContentLabel = DEFAULT_EXPAND_CONTENT_LABEL,
	collapseContentLabel = DEFAULT_COLLAPSE_CONTENT_LABEL,
	forceExpand = false,
	showOrderArrow = true,
	headerButtons,
	link: linkProp,
	orderButtonLabel,
	modulesHref
}) => {
	const linkFromContext = useContext(LinkContext)
	const link = linkProp ?? linkFromContext ?? DefaultLink
	const colorClass = `m-program-card--${program.color}`
	const accordionValue = program.expanded || forceExpand ? [`program-${program.id}`] : []

	const titleLines = splitTitle
		? splitProgramName(program.title)
		: { firstLine: program.title, secondLine: null }

	return (
		<div className={cn('m-program-card', colorClass, className)} style={style}>
			<ProgramCardHeader
				eyebrow={eyebrow}
				subtitle={program.subtitle}
				title={titleLines.firstLine}
				titleSecondLine={titleLines.secondLine}
				price={program.price}
				oldPrice={program.oldPrice}
				url={program.url}
				headerColor={program.color as THEME_COLORS_ENUM}
				featured={program.featured}
				iconsSpriteUrl={iconsSpriteUrl}
				headerButtons={headerButtons}
				link={link}
			/>

			<Accordion.Root type='multiple' defaultValue={accordionValue}>
				<Accordion.Item value={`program-${program.id}`}>
					<ProgramCardContent
						items={program.items}
						programId={program.id}
						expandContentLabel={expandContentLabel}
						collapseContentLabel={collapseContentLabel}
						forceExpand={forceExpand}
					/>
					<ProgramCardFooter
						programId={program.id}
						href={program.orderUrl}
						color={program.color as THEME_COLORS_ENUM}
						price={program.price}
						oldPrice={program.oldPrice}
						icon={iconsSpriteUrl}
						showOrderArrow={showOrderArrow}
						link={link}
						orderButtonLabel={orderButtonLabel}
						modulesHref={modulesHref}
					/>
				</Accordion.Item>
			</Accordion.Root>
		</div>
	)
}

MProgramCard.displayName = 'MProgramCard'
